import React from 'react';
import throttle from 'lodash.throttle';

import * as S from './styles';

const DEFAULT_HEADER_HEIGHT = 70;
const DEFAULT_TOP_MARGIN = 10;

const hasNativeStickySupport = () => {
  return (
    window.CSS &&
    window.CSS.supports &&
    window.CSS.supports('position', 'sticky')
  );
};

const updatePosition = (
  track: React.RefObject<HTMLDivElement>,
  element: React.RefObject<HTMLDivElement>,
  position: StickyTrackState['position'],
  headerHeight: StickyTrackProps['headerHeight'],
  topMargin: StickyTrackProps['topMargin'],
  setState: {
    (value: React.SetStateAction<StickyTrackState>): void;
  }
) => {
  let newPosition: StickyTrackState['position'];
  let width;
  // Note: the bounding client rect is relative to the *viewport*, not the page.
  const trackRect = track.current
    ? track.current.getBoundingClientRect()
    : null;
  if (!trackRect) {
    return;
  }
  if (trackRect.top >= headerHeight + topMargin) {
    newPosition = 'top';
  } else {
    const stickyRect = element.current
      ? element.current.getBoundingClientRect()
      : null;
    if (!stickyRect) {
      return;
    }
    if (trackRect.bottom - stickyRect.height < headerHeight + topMargin) {
      newPosition = 'bottom';
    } else {
      newPosition = 'middle';
    }

    width = stickyRect.width;
  }

  // Avoid unnecessary updates
  if (newPosition !== position) {
    setState({ position, width });
  }
};

type StickyTrackProps = {
  renderElement: React.ReactNode;
  children: React.ReactNode;
  headerHeight: number;
  topMargin: number;
};

type StickyTrackState = {
  position: 'top' | 'middle' | 'bottom';
  width?: number;
};

const StickyTrack = (props: StickyTrackProps) => {
  const {
    renderElement,
    children,
    headerHeight = DEFAULT_HEADER_HEIGHT,
    topMargin = DEFAULT_TOP_MARGIN,
  } = props;
  const [state, setState] = React.useState<StickyTrackState>({
    position: 'top',
  });
  const track = React.useRef<HTMLDivElement>(null);
  const element = React.useRef<HTMLDivElement>(null);
  const { position, width } = state;

  const throttledScrollHandler = React.useCallback(
    () =>
      throttle(
        () =>
          updatePosition(
            track,
            element,
            position,
            headerHeight,
            topMargin,
            setState
          ),
        300
      ),
    [headerHeight, position, topMargin]
  );

  React.useEffect(() => {
    if (!hasNativeStickySupport()) {
      window.addEventListener('scroll', throttledScrollHandler, false);
      updatePosition(
        track,
        element,
        position,
        headerHeight,
        topMargin,
        setState
      );
    }
    return () => {
      if (!hasNativeStickySupport()) {
        window.removeEventListener('scroll', throttledScrollHandler, false);
      }
    };
  }, [headerHeight, position, props, state, throttledScrollHandler, topMargin]);

  const stickyElementStyle = React.useMemo(
    () => ({ top: headerHeight + topMargin, width }),
    [headerHeight, topMargin, width]
  );

  return (
    <S.StickyTrack ref={track}>
      <S.StickyElement
        position={position}
        headerHeight={headerHeight}
        topMargin={topMargin}
        ref={element}
        style={stickyElementStyle}
      >
        {renderElement}
      </S.StickyElement>
      {children}
    </S.StickyTrack>
  );
};

export default StickyTrack;
